import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'services/Translation'
import { usePatientRouteParams } from 'components/patient/Parameters'
import { useStatusTranslation, getStatusText, getStatusColor } from 'services/Registrations'
import { CS_m } from 'back-end-api'
import styled from 'styled-components'
import { StatusHistoryModal } from 'components/common/StatusHistory'
import useDoctorMap from 'hooks/useDoctorMap'
import RescheduleModal from '../RescheduleModal'

export const StatusText = styled.div`
  color: ${({ color }) => color};
  display: flex;
  align-items: center;
`

export const TimeText = styled.div`
  margin-right: 5px;
`

// Branded hover tooltip used to reveal "sent by Dr. X" on the Advice Sent status.
// Mirrors the dark tooltip style used for the patient-list lock indicator.
const TooltipTrigger = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;
  border-bottom: 1px dotted currentColor;

  .status-tooltip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    opacity: 0;
    visibility: hidden;
    transform: translateX(-50%) translateY(4px);
    transition: opacity 0.15s ease, transform 0.15s ease;
    position: absolute;
    bottom: 100%;
    left: 50%;
    background: #202336;
    color: #fff;
    font-size: 0.6875rem;
    font-family: coresanscr55, sans-serif;
    line-height: 1;
    padding: 7px 11px;
    border-radius: 8px;
    white-space: nowrap;
    pointer-events: none;
    z-index: 9999;
    margin-bottom: 9px;
    box-shadow: 0 6px 18px rgba(32, 35, 54, 0.22);

    svg {
      flex-shrink: 0;
      opacity: 0.85;
    }

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 5px solid transparent;
      border-top-color: #202336;
    }
  }

  &:hover .status-tooltip,
  &:focus-visible .status-tooltip {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
`

const RescheduleButton = styled.button<{ disabled: boolean }>`
  background: none;
  border: none;
  color: ${({ disabled }) => (disabled ? '#999' : '#EB7C0C')};
  font-size: 14px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  text-decoration: underline;
  padding: 0 12px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.5 : 0.8)};
  }
`

const HistoryButton = styled.button`
  background: none;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 8px;
  padding: 0;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    background: rgba(235, 124, 12, 0.1);
    border-color: #EB7C0C;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    reschedule: getText('Reschedule', 'Patient_Details_Status'),
    statusHistory: getText('Status History', 'Patient_Details_Status'),
    adviceRead: getText('Advice read', 'Patient_Details_Status'),
    avLabel: getText('AV', 'Patient_Details_Status'),
    sentBy: getText('sent by Dr. %s', 'Patient_Details_Status'),
  }[key])

// Resolve the account id of the doctor who sent the advice: prefer the advice
// author/doctor, fall back to the status event that set status to adviceSent.
const getAdviceSenderId = (
  summary: CS_m.ModelsSummary | null | undefined
): number | undefined => {
  if (!summary) return undefined
  const fromAdvice = summary.advice?.doctor_account_id ?? summary.advice?.author_account_id
  if (fromAdvice) return fromAdvice
  const event = summary.status_events?.find(
    e => e.status === CS_m.ModelsSummaryStatus.adviceSent
  )
  return event?.changed_by_account_id
}

const AV_STATUS_COLORS: Record<string, string> = {
  pending: '#8B5CF6',
  accepted: '#7C3AED',
  ready: '#06B6D4',
  inProgress: '#2563EB',
  done: '#10B981',
  declined: '#EF4444',
  cancelled: '#EF4444',
  timeout: '#DC2626',
  missed: '#B91C1C',
}

const AV_STATUS_LABEL: Record<string, string> = {
  pending: 'AppointmentPending',
  accepted: 'AppointmentAccepted',
  ready: 'AppointmentReady',
  inProgress: 'AppointmentInProgress',
  done: 'AppointmentDone',
  declined: 'AppointmentDeclined',
  cancelled: 'AppointmentCancelled',
  timeout: 'AppointmentTimeout',
  missed: 'AppointmentMissed',
}

// Map summary status to its own RegistrationStatus label, ignoring appointments
const getSummaryOnlyStatusText = (
  summary: CS_m.ModelsSummary | null | undefined,
  t: (key: string) => string
): string | null => {
  if (!summary?.status) return null
  switch (summary.status) {
    case CS_m.ModelsSummaryStatus.inProgress: return t('SummaryInProgress')
    case CS_m.ModelsSummaryStatus.submitted: return t('SummarySubmitted')
    case CS_m.ModelsSummaryStatus.adviceSent: return t('SummaryAdviceSent')
    case CS_m.ModelsSummaryStatus.referralSent: return t('SummaryReferralSent')
    case CS_m.ModelsSummaryStatus.referralSpecialSent: return t('SummaryReferralSpecialSent')
    case CS_m.ModelsSummaryStatus.consulted: return t('SummaryConsulted')
    case CS_m.ModelsSummaryStatus.cancelled: return t('SummaryCancelled')
    case CS_m.ModelsSummaryStatus.timeout: return t('SummaryTimeout')
    default: return null
  }
}

const getSummaryOnlyStatusColor = (summary: CS_m.ModelsSummary | null | undefined): string => {
  if (!summary?.status) return '#EB7C0C'
  switch (summary.status) {
    case CS_m.ModelsSummaryStatus.inProgress: return '#3B82F6'
    case CS_m.ModelsSummaryStatus.submitted: return '#E6A817'
    case CS_m.ModelsSummaryStatus.adviceSent: return '#7C3AED'
    case CS_m.ModelsSummaryStatus.referralSent: return '#7C3AED'
    case CS_m.ModelsSummaryStatus.referralSpecialSent: return '#7C3AED'
    case CS_m.ModelsSummaryStatus.consulted: return '#15803D'
    case CS_m.ModelsSummaryStatus.cancelled: return '#9CA3AF'
    case CS_m.ModelsSummaryStatus.timeout: return '#F97316'
    default: return '#EB7C0C'
  }
}

const Status = () => {
  const { formatDate } = useTranslation()
  const { t } = useTranslation(texts)
  const tStatus = useStatusTranslation()

  const { registration, appointment, summary, mutateRegistration } = usePatientRouteParams()
  const doctorMap = useDoctorMap()
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)

  const handleOpenReschedule = useCallback(() => {
    if (appointment) {
      setShowRescheduleModal(true)
    }
  }, [appointment])

  const handleCloseReschedule = useCallback(() => {
    setShowRescheduleModal(false)
  }, [])

  const handleRescheduleSuccess = useCallback(() => {
    // Trigger SWR revalidation to fetch fresh registration data (including new appointment)
    mutateRegistration()
  }, [mutateRegistration])

  if (!registration) return null

  // Summary-level status (e.g. "Referral Sent", "Advice Sent") — always based on summary.status
  const summaryStatusText = getSummaryOnlyStatusText(summary, tStatus)
  const summaryStatusColor = getSummaryOnlyStatusColor(summary)

  // Summary timestamp: when the summary was last updated (e.g. when referral was sent)
  const summaryDate = summary?.updated_at
    ? new Date(summary.updated_at)
    : new Date(registration.created_at)

  // Appointment status (if an appointment exists)
  const hasAppointment = !!appointment?.status
  const avStatusText = hasAppointment ? tStatus(AV_STATUS_LABEL[appointment!.status] ?? '') : null
  const avStatusColor = hasAppointment ? (AV_STATUS_COLORS[appointment!.status] || '#8B5CF6') : '#8B5CF6'
  const avDate = appointment?.datetime ? new Date(appointment.datetime) : (appointment?.updated_at ? new Date(appointment.updated_at) : null)

  // Check if appointment can be rescheduled (only pending or accepted appointments)
  const canReschedule = appointment && 
    (appointment.status === CS_m.ModelsAppointmentStatus.pending ||
     appointment.status === CS_m.ModelsAppointmentStatus.accepted)

  // If no appointment, fall back to the combined status (same as before)
  const mainStatus = hasAppointment ? summaryStatusText : getStatusText(registration, tStatus)
  const mainColor = hasAppointment ? summaryStatusColor : getStatusColor(registration)
  const mainDate = summaryDate

  const adviceIsRead = summary?.advice?.is_read === true

  // "sent by Dr. X" label, shown next to an "Advice Sent" summary status
  const isAdviceSent = summary?.status === CS_m.ModelsSummaryStatus.adviceSent
  const adviceSenderName = isAdviceSent
    ? doctorMap.get(getAdviceSenderId(summary) ?? -1)
    : undefined

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-row items-center">
        <StatusText className={`text-sm pr-6 'border-r border-grey-100'`} color={mainColor}>
          {/* Clipboard/document icon for summary status */}
          <span style={{ width: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginRight: 8, flexShrink: 0 }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={mainColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'relative', left: -2 }}>
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
            </svg>
          </span>
          {adviceSenderName ? (
            <TooltipTrigger tabIndex={0}>
              {mainStatus}
              <span className="status-tooltip" role="tooltip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {t('sentBy').replace('%s', adviceSenderName)}
              </span>
            </TooltipTrigger>
          ) : (
            mainStatus
          )}
        </StatusText>
        <HistoryButton
          onClick={() => setShowHistoryModal(true)}
          title={t('statusHistory')}
          aria-label={t('statusHistory')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke={mainColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </HistoryButton>
        <StatusText className="text-sm pl-6 " color={mainColor}>
          <TimeText>{`${formatDate(mainDate, 'HH:mm')},`}</TimeText>
          {formatDate(mainDate, 'dd MMMM yyyy')}
        </StatusText>
        {!hasAppointment && (
          <RescheduleButton
            disabled={!canReschedule}
            onClick={handleOpenReschedule}
            title={!canReschedule ? 'No appointment to reschedule' : ''}
          >
            {t('reschedule')}
          </RescheduleButton>
        )}
        {/* Advice read badge — positioned inline on the summary row */}
        {adviceIsRead && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              marginLeft: 16,
              padding: '2px 10px',
              borderRadius: 12,
              background: '#F0FDF4',
              color: '#10B981',
              fontSize: '0.75rem',
              fontFamily: 'coresanscr55, sans-serif',
              whiteSpace: 'nowrap',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            {t('adviceRead')}
          </span>
        )}
        </div>
        {hasAppointment && avStatusText && (
          <div className="flex flex-row items-center" style={{ marginTop: 6 }}>
            <StatusText className="text-sm pr-6" color={avStatusColor}>
              {/* Video camera icon for appointment status */}
              <span style={{ width: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginRight: 8, flexShrink: 0 }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={avStatusColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
              </span>
              {t('avLabel')}: {avStatusText}
            </StatusText>
            {avDate && (
              <StatusText className="text-sm" color={avStatusColor}>
                <TimeText>{`${formatDate(avDate, 'HH:mm')},`}</TimeText>
                {formatDate(avDate, 'dd MMMM yyyy')}
              </StatusText>
            )}
            <RescheduleButton
              disabled={!canReschedule}
              onClick={handleOpenReschedule}
              title={!canReschedule ? 'No appointment to reschedule' : ''}
            >
              {t('reschedule')}
            </RescheduleButton>
          </div>
        )}
      </div>
      
      {appointment && (
        <RescheduleModal
          visible={showRescheduleModal}
          onClose={handleCloseReschedule}
          appointment={appointment}
          onSuccess={handleRescheduleSuccess}
        />
      )}

      <StatusHistoryModal
        visible={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        registration={registration}
      />
    </>
  )
}

Status.whyDidYouRender = true

export default memo(Status)
